/**
 * Front-end controller for the Quiz block.
 *
 * Reads server-rendered markup:
 *   .quiz[data-config]           → { continueLabel, calculateLabel }
 *   .quiz-track > .quiz-panel    → one per question
 *     .quiz-option[data-index]   → option button, carrying data-weight
 *   .quiz-counter                → "3/5" position readout
 *   .quiz-pagination             → filled with one .quiz-dot per question
 *   .quiz-results .quiz-result-card[data-min-score][data-max-score]
 *     .quiz-result-score[data-template] → score line with {score}/{total} tokens
 *
 * Answers stay changeable; the score is the sum of the selected weights. Every
 * question must be answered before the final "Calcular" button enables.
 */

const OPEN_RANGE = Number.POSITIVE_INFINITY;
const HALF = 2;

export default class QuizController {
  constructor(root) {
    this.root = root;
    const config = JSON.parse(root.dataset.config || '{}');
    this.continueLabel = config.continueLabel || 'Continuar';
    this.calculateLabel = config.calculateLabel || 'Calcular';

    this.track = root.querySelector('.quiz-track');
    this.viewport = root.querySelector('.quiz-viewport');
    this.panels = Array.from(root.querySelectorAll('.quiz-panel'));
    this.pagination = root.querySelector('.quiz-pagination');
    this.counter = root.querySelector('.quiz-counter');
    this.prevBtn = root.querySelector('.quiz-arrow.prev');
    this.advanceBtn = root.querySelector('.quiz-advance');
    this.note = root.querySelector('.quiz-note');
    this.resultCards = Array.from(root.querySelectorAll('.quiz-result-card'));

    this.total = this.panels.length;
    this.index = 0;
    this.answers = {};

    if (!this.total) {
      return;
    }

    this.buildPagination();
    this.wireEvents();
    this.update();
    window.addEventListener('resize', () => this.center());
  }

  buildPagination() {
    if (!this.pagination) {
      return;
    }
    this.pagination.innerHTML = '';
    this.dots = this.panels.map(() => {
      const dot = document.createElement('span');
      dot.className = 'quiz-dot';
      this.pagination.appendChild(dot);
      return dot;
    });
  }

  wireEvents() {
    this.panels.forEach((panel, panelIndex) => {
      panel.querySelectorAll('.quiz-option').forEach((button) => {
        button.addEventListener('click', () => {
          if (panelIndex === this.index) {
            this.select(Number(button.dataset.index));
          }
        });
      });
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.go(this.index - 1));
    }
    if (this.advanceBtn) {
      this.advanceBtn.addEventListener('click', () => this.advance());
    }
  }

  isAnswered(index) {
    return this.answers[index] !== undefined;
  }

  select(optionIndex) {
    const panel = this.panels[this.index];
    this.answers[this.index] = optionIndex;

    panel.querySelectorAll('.quiz-option').forEach((option, i) => {
      option.classList.toggle('selected', i === optionIndex);
    });

    this.update();
  }

  go(target) {
    if (target < 0 || target >= this.total) {
      return;
    }
    this.index = target;
    this.update();
  }

  update() {
    this.panels.forEach((panel, i) => {
      panel.classList.toggle('active', i === this.index);
    });
    this.center();

    if (this.dots) {
      // Answered is not the same as visited: you can go back and leave a
      // question blank, so this keys off whether an answer exists.
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('answered', this.isAnswered(i));
        dot.classList.toggle('current', i === this.index);
      });
    }

    if (this.counter) {
      this.counter.textContent = `${this.index + 1}/${this.total}`;
    }

    if (this.prevBtn) {
      this.prevBtn.disabled = this.index === 0;
    }

    this.updateButton();
  }

  center() {
    const panel = this.panels[this.index];
    if (!panel || !this.viewport) {
      return;
    }
    const offset =
      this.viewport.clientWidth / HALF -
      (panel.offsetLeft + panel.offsetWidth / HALF);
    this.track.style.transform = `translateX(${offset}px)`;
  }

  isComplete() {
    return this.panels.every((_, i) => this.isAnswered(i));
  }

  updateButton() {
    if (!this.advanceBtn) {
      return;
    }
    const last = this.index === this.total - 1;
    const complete = this.isComplete();
    this.advanceBtn.textContent = last ? this.calculateLabel : this.continueLabel;
    this.advanceBtn.disabled = last && !complete;
    if (this.note) {
      this.note.classList.toggle('is-shown', last && !complete);
    }
  }

  advance() {
    if (this.advanceBtn && this.advanceBtn.disabled) {
      return;
    }
    if (this.index === this.total - 1) {
      this.showResult();
    } else {
      this.go(this.index + 1);
    }
  }

  computeScore() {
    return this.panels.reduce((sum, panel, i) => {
      if (!this.isAnswered(i)) {
        return sum;
      }
      const option = panel.querySelectorAll('.quiz-option')[this.answers[i]];
      return sum + (option ? Number(option.dataset.weight) || 0 : 0);
    }, 0);
  }

  matchResult(score) {
    return (
      this.resultCards.find((card) => {
        const min = Number(card.dataset.minScore) || 0;
        const rawMax = card.dataset.maxScore;
        const max = rawMax === '' || rawMax === undefined ? OPEN_RANGE : Number(rawMax);
        return score >= min && score <= max;
      }) || this.resultCards[this.resultCards.length - 1]
    );
  }

  showResult() {
    const score = this.computeScore();
    const card = this.matchResult(score);
    if (card) {
      const line = card.querySelector('.quiz-result-score');
      if (line && line.dataset.template) {
        line.innerHTML = line.dataset.template
          .replace(/\{score\}/g, score)
          .replace(/\{total\}/g, this.total);
      }
      this.resultCards.forEach((c) => c.classList.toggle('is-shown', c === card));
    }
    this.root.classList.add('is-result');
  }
}
